import { useAtomValue } from 'jotai';
import queryString from 'query-string';
import { useMemo } from 'react';

import { selectedCountryCodesAtom } from 'common/state/countryState';

import { useDashboardRoute } from './useDashboardRoute';

export const LOOKER_ORIGIN = 'looker.wolt.com';
const LOOKER_DASHBOARDS_URL = `https://${LOOKER_ORIGIN}/embed/dashboards-next/`;

const decodeDashboardParams = (dashboardParams?: string | null) => {
  if (!dashboardParams) return '';
  let decoded: string;
  try {
    decoded = atob(dashboardParams);
  } catch {
    // if atob fails, this is probably a link that was no encoded originally
    decoded = dashboardParams;
  }
  return decoded;
};

export const getDashboardParams = () => {
  const UrlSearchParams = new URL(document.URL).searchParams;
  const dashboardParams = UrlSearchParams.get('dashboardParams');
  return decodeDashboardParams(dashboardParams);
};

/**
 * Returns url for Looker Dashboard embedding of iframe
 */
const getEmbedUrl = ({ dashboardId, countries }: { dashboardId: string; countries: string[] }) => {
  let url = `${LOOKER_DASHBOARDS_URL}${dashboardId}?allow_login_screen=true&embed_domain=${window.location.origin}&sdk=2`;
  // dashboardParams is a string of filter parameters created by use of filters in the embed
  // skip useSearch for dashboardParams because as these should not effect rendering
  // only the dashboardId change (indicating a new tab) should trigger embedUrl to be recomputed, causing the embed to be recreated
  const dashboardParams = getDashboardParams();
  if (dashboardParams) {
    url += `&${dashboardParams}`;
  }
  if (!url.includes('Country')) {
    url += `&${queryString.stringify({ Country: countries }, { arrayFormat: 'comma' })}`;
  }
  return url;
};

const useEmbedUrl = () => {
  const { dashboardId } = useDashboardRoute();
  const selectedCountries = useAtomValue(selectedCountryCodesAtom);

  const embedUrl = useMemo(() => {
    if (!dashboardId) {
      return '';
    }
    return getEmbedUrl({
      dashboardId,
      // at least pricing dashboard will not load properly without country filter
      countries: selectedCountries.length ? selectedCountries : ['FIN'],
    });
  }, [dashboardId, selectedCountries]);

  return embedUrl;
};

export default useEmbedUrl;
